import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import {
  setTokens,
  setAuthLoading,
  setAuthError,
  setPasswordResetStep,
  setPasswordResetEmail,
  setPasswordResetToken,
  resetPasswordResetFlow,
  resetAuth,
  PasswordResetStep,
} from '../store/slices/authSlice';
import { setEmail, logout as logoutUser } from '../store/slices/userSlice';
import { showToast } from '../store/slices/uiSlice';
import {
  login as loginApi,
  logout as logoutApi,
  submitForgotPassword,
  verifyForgotPassword,
  changePassword,
  LoginRequest,
  ApiErrorResponse,
} from '../api/loginApi';
import {
  REIMBURSEMENT_LOGIN_WRONG_CREDENTIALS,
  REIMBURSEMENT_ACCOUNT_NOT_FOUND,
} from '../constants/responseStatuses';
import { AxiosError } from 'axios';

// ============================================
// Error Messages (German)
// ============================================
const ErrorMessages = {
  WRONG_CREDENTIALS: 'Ungültige E-Mail-Adresse oder Passwort',
  ACCOUNT_NOT_FOUND: 'Das Profil wurde nicht gefunden.',
  INVALID_CODE: 'Der Code ist falsch.',
  GENERIC_ERROR: 'Hoppla, etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
  NETWORK_ERROR: 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.',
} as const;

// ============================================
// Success Messages (German)
// ============================================
const SuccessMessages = {
  CODE_SENT: 'Wir haben Ihnen einen Code an Ihre E-Mail geschickt. Bitte prüfen Sie Ihr Postfach.',
  CODE_RESENT: 'Wir haben Ihnen einen neuen Code an Ihre E-Mail geschickt. Bitte prüfen Sie Ihr Postfach.',
  PASSWORD_CHANGED: 'Ihr Passwort wurde erfolgreich geändert.',
  LOGOUT: 'Sie wurden erfolgreich abgemeldet.',
} as const;

// ============================================
// Hook
// ============================================
export const useAuth = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { isAuthenticated, isLoading, error, passwordReset } = useAppSelector(
    (state) => state.auth
  );

  // ============================================
  // Helper: Extract error message from API error
  // ============================================
  const getErrorMessage = useCallback((err: unknown): string => {
    if (err instanceof AxiosError) {
      const apiError = err.response?.data as ApiErrorResponse | undefined;
      
      if (apiError?.errorCode === REIMBURSEMENT_LOGIN_WRONG_CREDENTIALS) {
        return ErrorMessages.WRONG_CREDENTIALS;
      }
      if (apiError?.errorCode === REIMBURSEMENT_ACCOUNT_NOT_FOUND) {
        return ErrorMessages.ACCOUNT_NOT_FOUND;
      }
      if (apiError?.errorCode === 'REIMBURSEMENT_ACCOUNT_FORGOT_PASSWORD_INVALID_CODE') {
        return ErrorMessages.INVALID_CODE;
      }
      if (!err.response) {
        return ErrorMessages.NETWORK_ERROR;
      }
    }
    return ErrorMessages.GENERIC_ERROR;
  }, []);

  // ============================================
  // Login
  // ============================================
  const login = useCallback(
    async (credentials: LoginRequest): Promise<boolean> => {
      dispatch(setAuthLoading(true));
      dispatch(setAuthError(null));

      try {
        const response = await loginApi(credentials);
        dispatch(setTokens({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        }));
        dispatch(setEmail(credentials.email));
        return true;
      } catch (err) {
        const message = getErrorMessage(err);
        dispatch(setAuthError(message));
        return false;
      } finally {
        dispatch(setAuthLoading(false));
      }
    },
    [dispatch, getErrorMessage]
  );

  // ============================================
  // Logout
  // ============================================
  const logout = useCallback(() => {
    logoutApi();
    dispatch(resetAuth());
    dispatch(logoutUser());
    dispatch(showToast({ type: 'success', body: SuccessMessages.LOGOUT }));
    navigate('/');
  }, [dispatch, navigate]);

  // ============================================
  // Password Reset: Step 1 - Submit Email
  // ============================================
  const requestPasswordReset = useCallback(
    async (email: string): Promise<boolean> => {
      dispatch(setAuthLoading(true));
      dispatch(setAuthError(null));

      try {
        await submitForgotPassword({ email });
        dispatch(setPasswordResetEmail(email));
        dispatch(setPasswordResetStep(1));
        dispatch(showToast({ type: 'success', body: SuccessMessages.CODE_SENT }));
        return true;
      } catch (err) {
        const message = getErrorMessage(err);
        dispatch(setAuthError(message));
        return false;
      } finally {
        dispatch(setAuthLoading(false));
      }
    },
    [dispatch, getErrorMessage]
  );

  // ============================================
  // Password Reset: Resend Code
  // ============================================
  const resendVerificationCode = useCallback(async (): Promise<boolean> => {
    dispatch(setAuthLoading(true));

    try {
      await submitForgotPassword({ email: passwordReset.email });
      dispatch(showToast({ type: 'success', body: SuccessMessages.CODE_RESENT }));
      return true;
    } catch (err) {
      const message = getErrorMessage(err);
      dispatch(setAuthError(message));
      return false;
    } finally {
      dispatch(setAuthLoading(false));
    }
  }, [dispatch, getErrorMessage, passwordReset.email]);

  // ============================================
  // Password Reset: Step 2 - Verify Code
  // ============================================
  const verifyResetCode = useCallback(
    async (code: string): Promise<boolean> => {
      dispatch(setAuthLoading(true));
      dispatch(setAuthError(null));

      try {
        const response = await verifyForgotPassword({
          email: passwordReset.email,
          code,
        });
        dispatch(setPasswordResetToken(response.token));
        dispatch(setPasswordResetStep(2));
        return true;
      } catch (err) {
        const message = getErrorMessage(err);
        dispatch(setAuthError(message));
        return false;
      } finally {
        dispatch(setAuthLoading(false));
      }
    },
    [dispatch, getErrorMessage, passwordReset.email]
  );

  // ============================================
  // Password Reset: Step 3 - Set New Password
  // ============================================
  const setNewPassword = useCallback(
    async (newPassword: string): Promise<boolean> => {
      dispatch(setAuthLoading(true));
      dispatch(setAuthError(null));

      try {
        await changePassword({
          email: passwordReset.email,
          token: passwordReset.token,
          password: newPassword,
        });

        // Auto-login after password change
        const loginSuccess = await login({
          email: passwordReset.email,
          password: newPassword,
        });

        if (loginSuccess) {
          dispatch(resetPasswordResetFlow());
          dispatch(showToast({ type: 'success', body: SuccessMessages.PASSWORD_CHANGED }));
          navigate('/profile');
          return true;
        }

        return false;
      } catch (err) {
        const message = getErrorMessage(err);
        dispatch(setAuthError(message));
        return false;
      } finally {
        dispatch(setAuthLoading(false));
      }
    },
    [dispatch, getErrorMessage, login, navigate, passwordReset.email, passwordReset.token]
  );

  // ============================================
  // Go back to previous step in password reset
  // ============================================
  const goToPasswordResetStep = useCallback(
    (step: PasswordResetStep) => {
      dispatch(setPasswordResetStep(step));
      dispatch(setAuthError(null));
    },
    [dispatch]
  );

  // ============================================
  // Reset password reset flow
  // ============================================
  const cancelPasswordReset = useCallback(() => {
    dispatch(resetPasswordResetFlow());
    dispatch(setAuthError(null));
  }, [dispatch]);

  // ============================================
  // Clear error
  // ============================================
  const clearError = useCallback(() => {
    dispatch(setAuthError(null));
  }, [dispatch]);

  return {
    // State
    isAuthenticated,
    isLoading,
    error,
    passwordResetStep: passwordReset.step,
    passwordResetEmail: passwordReset.email,

    // Auth actions
    login,
    logout,
    clearError,

    // Password reset actions
    requestPasswordReset,
    resendVerificationCode,
    verifyResetCode,
    setNewPassword,
    goToPasswordResetStep,
    cancelPasswordReset,
  };
};

export default useAuth;

